import { formatCurrency } from "./Utils";

/**
 * Conversion Fouta Bio : prix vendeur en FCFA (XOF) → Ouguiya (MRU).
 * Taux de référence XE : 1 XOF = 0,070422 MRU
 * (ex. 15 000 FCFA → ≈ 1 056 MRU).
 */
export const XOF_TO_MRU_RATE = 0.070422;

/** @deprecated Utiliser XOF_TO_MRU_RATE ; conservé pour compat (~14,2 XOF pour 1 MRU). */
export const XOF_PER_MRU = 1 / XOF_TO_MRU_RATE;

export function convertXofToMru(amountXof: number): number {
  if (!Number.isFinite(amountXof) || amountXof <= 0) return 0;
  return Math.round(amountXof * XOF_TO_MRU_RATE);
}

export function formatMruAmount(amountXof: number, prefix = ""): string | undefined {
  const mru = convertXofToMru(amountXof);
  if (mru <= 0) return undefined;
  return `${prefix}${mru.toLocaleString("fr-FR")} MRU`;
}

export function getPromoPrice(prix: string | number, taux: number): number {
  const pr = typeof prix === "string" ? parseFloat(prix) : prix;
  if (!Number.isFinite(pr)) return 0;
  return pr * (1 - taux / 100);
}

export function getProductPromoRate(product: any): number {
  if (!product) return 0;
  const cat = product?.categorie?.promotion;
  const sub = product?.sous_categorie?.promotion;
  const prod = product?.promotion;
  if (cat?.is_active) return Number(cat.taux) || 0;
  if (sub?.is_active) return Number(sub.taux) || 0;
  if (prod?.is_active) return Number(prod.taux) || 0;
  return 0;
}

export function hasActivePromotion(product: any): boolean {
  return !!(
    product?.categorie?.promotion?.is_active ||
    product?.sous_categorie?.promotion?.is_active ||
    product?.promotion?.is_active
  );
}

const VAR_SIZE_TYPES = [
  "taille_unique_avec_couleur",
  "taille_variable",
  "taille_variable_prix_unique",
];

export function isVarSizeProduct(type?: string): boolean {
  if (!type) return false;
  return VAR_SIZE_TYPES.includes(type);
}

/** Produit à tailles variables avec prix par variation — choix obligatoire sur la fiche. */
export function requiresSizeSelection(type?: string): boolean {
  return type === "taille_variable";
}

function normalizeOptionalBool(value: unknown): boolean | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "true" || normalized === "1") return true;
  if (normalized === "false" || normalized === "0" || normalized === "") {
    return false;
  }
  return undefined;
}

/** false / null = gestion de stock désactivée (stock illimité). */
export function isStockTrackingEnabled(product: any): boolean {
  if (!product) return true;
  if (product.stock_unlimited === true) return false;
  const tracking = normalizeOptionalBool(product.stock_tracking);
  return tracking !== false && tracking !== null;
}

export function isVariationStockUnlimited(variation: any): boolean {
  if (!variation) return false;
  const managed = normalizeOptionalBool(variation.stock_management);
  return managed === false;
}

export function isVariationAvailable(product: any, variation: any): boolean {
  if (!variation || variation.active === false) return false;
  if (!isStockTrackingEnabled(product)) return true;
  if (isVariationStockUnlimited(variation)) return true;
  return Number(variation?.quantite) > 0;
}

export function getPurchasableVariations(product: any): any[] {
  return (product?.variations ?? []).filter((variation: any) =>
    isVariationAvailable(product, variation),
  );
}

/**
 * Quantité max pour le panier.
 * Illimitée si stock produit désactivé, stock_unlimited, ou stock_management=false sur la variation.
 */
export function getCartStockLimit(product: any, variation?: any): number {
  if (!isStockTrackingEnabled(product)) return 99999;
  if (variation && isVariationStockUnlimited(variation)) return 99999;

  if (variation) {
    const qty = Number(variation?.quantite);
    return Number.isFinite(qty) && qty > 0 ? qty : 0;
  }

  const stock = Number(product?.stock);
  return Number.isFinite(stock) && stock > 0 ? stock : 0;
}

/** Quantité max sélectionnable (UI compteur / modal taille). */
export function getMaxOrderQuantity(product: any, variation?: any): number {
  const limit = getCartStockLimit(product, variation);
  return limit > 0 ? limit : 0;
}

/** Variations en stock avec taille distincte (sélecteur carte produit). */
export function getSizeVariationsForPicker(product: any): any[] {
  const variations = product?.variations ?? [];
  const trackingOff = !isStockTrackingEnabled(product);
  const inStock = variations.filter(
    (v: any) =>
      v.active !== false &&
      (trackingOff ||
        isVariationStockUnlimited(v) ||
        Number(v?.quantite) > 0) &&
      v?.taille != null &&
      String(v.taille).trim() !== "",
  );
  return inStock.filter(
    (v: any, index: number, self: any[]) =>
      self.findIndex((x) => x.taille === v.taille) === index,
  );
}

/** Afficher le sélecteur « Choisir la taille » uniquement s'il y a 2+ tailles. */
export function needsSizePickerOnCard(product: any): boolean {
  return (
    requiresSizeSelection(product?.type) &&
    getSizeVariationsForPicker(product).length > 1
  );
}

/**
 * Disponibilité à l'achat / affichage.
 * Si la gestion de stock est désactivée, le produit n'est jamais « indisponible ».
 */
export function isProductAvailable(product: any): boolean {
  if (!product) return false;
  if (!isStockTrackingEnabled(product)) return true;

  const variations = product?.variations ?? [];
  if (variations.length > 0) {
    if (variations.some(isVariationStockUnlimited)) return true;
    return variations.some(
      (v: any) => v.active !== false && Number(v?.quantite) > 0,
    );
  }

  return Number(product?.stock) > 0;
}

/** Première variation en stock utilisable pour le panier (liste / cartes). */
export function getDefaultCartVariation(product: any): any | undefined {
  const sizeOptions = getSizeVariationsForPicker(product);
  if (sizeOptions.length > 0) return sizeOptions[0];

  const variations = product?.variations ?? [];
  if (!variations.length) return undefined;
  const inStock = variations.filter(
    (v: any) =>
      v.active !== false &&
      (isStockTrackingEnabled(product) === false ||
        isVariationStockUnlimited(v) ||
        Number(v?.quantite) > 0),
  );
  return inStock[0] ?? variations[0];
}

export function productHasMultiplePrices(
  product: any,
  promoRate: number,
): boolean {
  if (product?.wholesale_tiers?.length > 0) return true;
  if (!product?.variations?.length) return false;
  if (
    product.variations.some(
      (v: any) => v.wholesale_tiers && v.wholesale_tiers.length > 0,
    )
  ) {
    return true;
  }
  const basePrice = getPromoPrice(parseFloat(String(product.prix)), promoRate);
  const prices = new Set(
    product.variations
      .filter((v: any) => v.active !== false)
      .map((v: any) =>
        v.prix != null && v.prix !== ""
          ? getPromoPrice(parseFloat(String(v.prix)), promoRate)
          : basePrice,
      ),
  );
  return prices.size > 1;
}

export function getVariationPriceRange(
  product: any,
  promoRate: number,
): { min: number; max: number } {
  const basePrice = getPromoPrice(parseFloat(String(product?.prix ?? 0)), promoRate);
  if (!product?.variations?.length) {
    return { min: basePrice, max: basePrice };
  }
  const prices = product.variations
    .filter((v: any) => v.active !== false)
    .map((v: any) =>
      v.prix != null && v.prix !== ""
        ? getPromoPrice(parseFloat(String(v.prix)), promoRate)
        : basePrice,
    )
    .filter((n: number) => Number.isFinite(n));
  if (!prices.length) return { min: basePrice, max: basePrice };
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export function getVariationRawPriceRange(
  product: any,
): { min: number; max: number } {
  const base = parseFloat(String(product?.prix ?? 0));
  const safeBase = Number.isFinite(base) ? base : 0;
  if (!product?.variations?.length) {
    return { min: safeBase, max: safeBase };
  }
  const prices = product.variations
    .filter((v: any) => v.active !== false)
    .map((v: any) =>
      v.prix != null && v.prix !== ""
        ? parseFloat(String(v.prix))
        : safeBase,
    )
    .filter((n: number) => Number.isFinite(n));
  if (!prices.length) return { min: safeBase, max: safeBase };
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export function formatPriceRange(
  min: number,
  max: number,
  devise?: string,
): string {
  if (min === max) return formatCurrency(min, devise);
  return `${formatCurrency(min, devise)} - ${formatCurrency(max, devise)}`;
}

export function getUnitPriceForVariation(
  product: any,
  variation: any,
  promoRate: number,
): number {
  if (variation?.prix != null && variation?.prix !== "") {
    return getPromoPrice(parseFloat(String(variation.prix)), promoRate);
  }
  return getPromoPrice(parseFloat(String(product?.prix ?? 0)), promoRate);
}

export function isValidProductColor(
  couleur: string | null | undefined,
): boolean {
  if (couleur == null) return false;
  const trimmed = String(couleur).trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return false;
  return (
    /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed) ||
    /^rgba?\(/i.test(trimmed)
  );
}

export function getDisplayableColorVariations(
  variations: any[] | undefined,
  product?: any,
): any[] {
  if (!variations?.length) return [];
  return variations
    .filter((v) => isVariationAvailable(product ?? { variations }, v))
    .filter(
      (value, index, self) =>
        self.findIndex((v) => v.couleur === value.couleur) === index,
    )
    .filter((v) => isValidProductColor(v.couleur));
}

export function getCardPriceDisplay(
  product: any,
  devise?: string,
): ProductPriceLabels & { showMultipleSizesBadge: boolean; startingFrom: boolean } {
  const promoRate = getProductPromoRate(product);
  const hasPromo = hasActivePromotion(product) && promoRate > 0;
  const multiSize = needsSizePickerOnCard(product);
  const priceRange = getVariationPriceRange(product, promoRate);
  const rawRange = getVariationRawPriceRange(product);

  if (multiSize) {
    const minDisplay = formatCurrency(priceRange.min, devise);
    const minRaw = formatCurrency(rawRange.min, devise);
    return {
      showRange: false,
      startingFrom: true,
      showMultipleSizesBadge: true,
      hasPromo,
      display: `À partir de ${minDisplay}`,
      strikethrough: hasPromo ? `À partir de ${minRaw}` : undefined,
      displayMru: formatMruAmount(priceRange.min, "À partir de "),
    };
  }

  const labels = getProductPriceLabels(product, devise);
  return {
    ...labels,
    startingFrom: false,
    showMultipleSizesBadge: false,
  };
}

export function parseSizeValue(
  taille?: string,
): { value: number; unit: string } | null {
  if (!taille) return null;
  const match = String(taille)
    .trim()
    .match(/^([\d.,]+)\s*(.+)$/i);
  if (!match) return null;
  const value = parseFloat(match[1].replace(",", "."));
  if (!Number.isFinite(value) || value <= 0) return null;
  return { value, unit: match[2].trim().toLowerCase() };
}

/** Prix unitaire affiché (ex. 12 F/g, 180 F/ml). */
export function formatVariationUnitPrice(
  unitPrice: number,
  taille?: string,
): string | null {
  const parsed = parseSizeValue(taille);
  if (!parsed || unitPrice <= 0) return null;

  let baseValue = parsed.value;
  let unitLabel = parsed.unit;

  if (unitLabel === "kg") {
    baseValue *= 1000;
    unitLabel = "g";
  } else if (unitLabel === "l" || unitLabel === "litre" || unitLabel === "litres") {
    baseValue *= 1000;
    unitLabel = "ml";
  }

  const perUnit = Math.round(unitPrice / baseValue);
  if (!Number.isFinite(perUnit) || perUnit <= 0) return null;
  return `${perUnit} F/${unitLabel}`;
}

export type ProductPriceLabels = {
  showRange: boolean;
  hasPromo: boolean;
  display: string;
  strikethrough?: string;
  /** Conversion Ouguiya (MRU) depuis le prix FCFA */
  displayMru?: string;
};

function resolveUnitPriceForDisplay(
  product: any,
  promoRate: number,
  hasPromo: boolean,
): number {
  const isVar = isVarSizeProduct(product?.type);
  const priceRange = isVar
    ? getVariationPriceRange(product, promoRate)
    : {
        min: getPromoPrice(product?.prix ?? 0, hasPromo ? promoRate : 0),
        max: getPromoPrice(product?.prix ?? 0, hasPromo ? promoRate : 0),
      };
  const baseProductPrice = Number(product?.prix);
  const hasBasePrice = Number.isFinite(baseProductPrice) && baseProductPrice > 0;
  if (isVar && !hasBasePrice) return priceRange.min;
  return hasPromo
    ? getPromoPrice(baseProductPrice, promoRate)
    : baseProductPrice;
}

export function getProductPriceLabels(
  product: any,
  devise?: string,
): ProductPriceLabels {
  const promoRate = getProductPromoRate(product);
  const hasPromo = hasActivePromotion(product) && promoRate > 0;
  const isVar = isVarSizeProduct(product?.type);
  const hasMultiple =
    isVar && productHasMultiplePrices(product, promoRate);
  const priceRange = isVar
    ? getVariationPriceRange(product, promoRate)
    : {
        min: getPromoPrice(product?.prix ?? 0, hasPromo ? promoRate : 0),
        max: getPromoPrice(product?.prix ?? 0, hasPromo ? promoRate : 0),
      };
  const rawRange = isVar
    ? getVariationRawPriceRange(product)
    : {
        min: Number(product?.prix) || 0,
        max: Number(product?.prix) || 0,
      };

  const showRange =
    priceRange.min !== priceRange.max &&
    (isVar || hasMultiple);

  if (showRange) {
    return {
      showRange: true,
      hasPromo,
      display: formatPriceRange(priceRange.min, priceRange.max, devise),
      strikethrough: hasPromo
        ? formatPriceRange(rawRange.min, rawRange.max, devise)
        : undefined,
      displayMru: formatMruAmount(priceRange.min, "À partir de "),
    };
  }

  const unitPrice = resolveUnitPriceForDisplay(product, promoRate, hasPromo);
  const baseProductPrice = Number(product?.prix);
  const hasBasePrice = Number.isFinite(baseProductPrice) && baseProductPrice > 0;
  const rawUnitPrice = isVar && !hasBasePrice ? rawRange.min : baseProductPrice;

  return {
    showRange: false,
    hasPromo,
    display: formatCurrency(unitPrice, devise),
    strikethrough: hasPromo
      ? formatCurrency(rawUnitPrice, devise)
      : undefined,
    displayMru: formatMruAmount(unitPrice),
  };
}
